/**
 * Process module catalog hooks (PR 14 of the modularisation roadmap).
 *
 * Wraps GET ``/api/v1/processes`` so the FE module registry can
 * discover what the backend has registered. Cached per request — the
 * registry rarely changes, so a 10-minute stale time matches the
 * existing materials catalog hook.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { chainContinuationKeys } from "@/api/queries/chainContinuationKeys";
import { apiFetch } from "@/lib/api";
import type {
  AddOperationRequest,
  AdminProcess,
  ChainContinuation,
  ItemOperation,
  PlatformOperation,
  Process,
  ProcessManifest,
  ProcessVariantAxis,
} from "@/api/types";

const API = "/api/v1";

const processQueryKeys = {
  all: () => ["processes"] as const,
  list: (includeNonEntry: boolean) =>
    ["processes", { includeNonEntry }] as const,
  one: (slug: string) => ["processes", "by-slug", slug] as const,
  manifest: (slug: string) => ["processes", "manifest", slug] as const,
  variantAxis: (slug: string) => ["processes", "variant-axis", slug] as const,
  adminCatalog: () => ["processes", "admin-catalog"] as const,
  platform: () => ["processes", "platform-operations"] as const,
  chainContinuations: (itemSlug: string) =>
    chainContinuationKeys.cart(itemSlug),
} as const;

/**
 * Returns tenant-visible process modules.
 *
 * Default is the customer-facing set: the operations a customer of this
 * tenant may START with, read from the operation graph. Everything the
 * tenant offers only as a continuation of another operation is left out —
 * it belongs in a chain dropdown, not on the picker.
 *
 * Pass ``includeNonEntry`` for admin/catalog surfaces that need the whole
 * registry (label resolvers, rate tables, the graph editor palette).
 * Renamed from ``includeChainOnly`` in O4c: the flag it was named after
 * (``ProcessModuleDefinition.is_chain_only``) no longer exists, because
 * whether an operation can start a chain is a property of where the tenant
 * placed it, not of the operation.
 */
export function useProcesses(
  options?: { includeNonEntry?: boolean } & Omit<
    UseQueryOptions<Process[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { includeNonEntry = false, ...rest } = options ?? {};
  return useQuery<Process[]>({
    queryKey: processQueryKeys.list(includeNonEntry),
    queryFn: () =>
      apiFetch<Process[]>(
        `${API}/processes${includeNonEntry ? "?include_non_entry=true" : ""}`,
      ),
    staleTime: 10 * 60 * 1000,
    ...rest,
  });
}

/**
 * The operations the platform offers publicly, slim — what the marketing
 * landing renders (``GET /public/platform-operations``, KAN-1600). The
 * tenant catalog routes answer 404 on the platform host.
 */
export function usePlatformOperations(
  options?: Omit<UseQueryOptions<PlatformOperation[]>, "queryKey" | "queryFn">,
) {
  return useQuery<PlatformOperation[]>({
    queryKey: processQueryKeys.platform(),
    queryFn: () =>
      apiFetch<PlatformOperation[]>(`${API}/public/platform-operations`),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * The whole registry the tenant offers plus the admin-only fields (artifact
 * contracts, dimension domains, density) — ``GET /admin/process-catalog``,
 * behind the tenant-admin gate (KAN-1471). The graph editor computes edge
 * compatibility from it; customer surfaces use ``useProcesses``.
 */
export function useAdminProcessCatalog(
  options?: Omit<UseQueryOptions<AdminProcess[]>, "queryKey" | "queryFn">,
) {
  return useQuery<AdminProcess[]>({
    queryKey: processQueryKeys.adminCatalog(),
    queryFn: () => apiFetch<AdminProcess[]>(`${API}/admin/process-catalog`),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Single-module lookup by slug — used by ``/services/{slug}`` pages
 * (PR 17) before rendering the dropzone.
 */
export function useProcess(
  slug: string,
  options?: Omit<UseQueryOptions<Process>, "queryKey" | "queryFn">,
) {
  return useQuery<Process>({
    queryKey: processQueryKeys.one(slug),
    queryFn: () => apiFetch<Process>(`${API}/processes/${slug}`),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Admin-pricing manifest for one process — variant axis label,
 * pricing field set, and price unit. Drives the unified Ceník's
 * Materials → Variants → Modifiers shape so each tab renders
 * identically with only labels + units differing.
 *
 * Accepts either the process slug (``laser``, ``3d-tisk``) or the
 * stable ``ProcessType`` name (``laser``, ``print_3d``); the backend
 * resolves both via ``GET /admin/process-catalog/{slug-or-name}/manifest``
 * (tenant-admin gate since KAN-1471 — customer surfaces read the variant axis
 * through ``useProcessVariantAxis``).
 *
 * A process that isn't priced yet still answers 200, with a neutral
 * manifest (no pricing fields, and whatever variant axes it declares —
 * those don't depend on the ceník). 404 now means only "no such
 * process" (KAN-802). Stale time matches the parent registry —
 * manifests rarely change.
 */
function manifestQueryOptions(slugOrName: string) {
  return {
    queryKey: processQueryKeys.manifest(slugOrName),
    queryFn: () =>
      apiFetch<ProcessManifest>(
        `${API}/admin/process-catalog/${slugOrName}/manifest`,
      ),
    enabled: Boolean(slugOrName),
    staleTime: 10 * 60 * 1000,
  };
}

export function useProcessManifest(
  slugOrName: string | null | undefined,
  options?: Omit<UseQueryOptions<ProcessManifest>, "queryKey" | "queryFn">,
) {
  return useQuery<ProcessManifest>({
    ...manifestQueryOptions(slugOrName ?? ""),
    ...options,
  });
}

/** Admin manifests for many processes at once — one cached query per slug,
 *  same keys as the single-slug hook. Results are positional. */
export function useProcessManifests(slugsOrNames: readonly string[]) {
  return useQueries({
    queries: slugsOrNames.map((slug) => manifestQueryOptions(slug)),
  });
}

/**
 * The variant axis of one process — the public projection of the manifest
 * (``GET /processes/{slug}/manifest``, KAN-1471). What the customer
 * configurator and bulk-configure render; same 404 contract as the manifest.
 */
function variantAxisQueryOptions(slugOrName: string) {
  return {
    queryKey: processQueryKeys.variantAxis(slugOrName),
    queryFn: () =>
      apiFetch<ProcessVariantAxis>(`${API}/processes/${slugOrName}/manifest`),
    enabled: Boolean(slugOrName),
    staleTime: 10 * 60 * 1000,
  };
}

export function useProcessVariantAxis(
  slugOrName: string | null | undefined,
  options?: Omit<UseQueryOptions<ProcessVariantAxis>, "queryKey" | "queryFn">,
) {
  return useQuery<ProcessVariantAxis>({
    ...variantAxisQueryOptions(slugOrName ?? ""),
    ...options,
  });
}

/**
 * Variant axes for many processes at once — one cached query per slug.
 *
 * Backs the public material catalog, which renders the declared variant axis
 * (label + unit) of every process on the page rather than one selected
 * process's. Results are positional: index i is the axis for slug i, and a
 * slug that resolves to no process (404) stays `undefined` there.
 */
export function useProcessVariantAxes(slugsOrNames: readonly string[]) {
  return useQueries({
    queries: slugsOrNames.map((slug) => variantAxisQueryOptions(slug)),
  });
}

/**
 * ``property_key`` → display names of the processes declaring that property
 * as a variant axis (``manifest.variant_params[].property_key``).
 *
 * Backs the „tvoří varianty" badge in the category×property matrices: the
 * matrix is property-first, so it must SAY which properties are the grain —
 * their values form variants — instead of leaving that to be discovered on
 * an item detail. Loading/missing manifests simply contribute nothing.
 */
export function useVariantAxisPropertyProcesses(): Map<string, string[]> {
  const processesQuery = useProcesses({ includeNonEntry: true });
  const processes = Array.isArray(processesQuery.data)
    ? processesQuery.data
    : [];
  const manifests = useProcessManifests(processes.map((p) => p.slug || p.name));
  // Rebuilt per render — the lists are tiny and consumers only read it
  // while rendering, so memoising would just add stale-deps surface.
  const map = new Map<string, string[]>();
  processes.forEach((proc, i) => {
    const manifest = manifests[i]?.data;
    const params =
      manifest?.variant_params ??
      (manifest?.variant_param ? [manifest.variant_param] : []);
    for (const param of params) {
      if (!param?.property_key) continue;
      const names = map.get(param.property_key) ?? [];
      const label = proc.display_name || proc.name;
      if (!names.includes(label)) names.push(label);
      map.set(param.property_key, names);
    }
  });
  return map;
}

// ``useProcessTypeDefault(s)`` (čtení ``ProcessTypeDefault``) tu bývaly pro
// DimensionPicker — smazané: osy i skladová jednotka teď jedou z deklarací
// (manifesty procesů ∪ osy kategorie, ``useMaterialDimensions``), protože
// PTD řádek u operací založených přes UI neexistoval a formuláře se zamykaly
// na „schéma chybí". BE endpointy zatím stojí; vyřazení tabulky je další krok.

/**
 * Modules that can be appended after the current chain tail —
 * powers the "+ Přidat dokončení" dropdown (PR 20).
 */
export function useChainContinuations(
  itemSlug: string | undefined,
  options?: Omit<UseQueryOptions<ChainContinuation[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ChainContinuation[]>({
    queryKey: processQueryKeys.chainContinuations(itemSlug ?? ""),
    queryFn: () =>
      apiFetch<ChainContinuation[]>(
        `${API}/cart/items/${itemSlug}/chain-continuations`,
      ),
    enabled: Boolean(itemSlug),
    // Continuations depend on the chain state, which can change
    // when the user adds / removes ops — short stale time.
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Append an operation to an item's chain. Invalidates the
 * chain-continuations cache so the dropdown refreshes once the
 * new op lands.
 */
export function useAddOperation(
  itemSlug: string,
  options?: UseMutationOptions<ItemOperation, unknown, AddOperationRequest>,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<ItemOperation, unknown, AddOperationRequest>({
    mutationFn: (payload) =>
      apiFetch<ItemOperation>(`${API}/cart/items/${itemSlug}/operations`, {
        method: "POST",
        body: payload,
      }),
    ...restOptions,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({
        queryKey: processQueryKeys.chainContinuations(itemSlug),
      });
      qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/**
 * Persist a chain operation's config (anodize colour, brushed grit,
 * etc.) and re-run the pricing strategy server-side. Used by the
 * inline sub-cards on the configurator so the customer's choices
 * survive a refetch — and so any future per-knob price differences
 * flow through automatically.
 */
export function useUpdateOperationConfig(
  itemSlug: string,
  options?: UseMutationOptions<
    ItemOperation,
    unknown,
    { operationId: number; config: Record<string, unknown> }
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    ItemOperation,
    unknown,
    { operationId: number; config: Record<string, unknown> }
  >({
    mutationFn: ({ operationId, config }) =>
      apiFetch<ItemOperation>(`${API}/cart/operations/${operationId}`, {
        method: "PATCH",
        body: { config },
      }),
    ...restOptions,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({
        queryKey: processQueryKeys.chainContinuations(itemSlug),
      });
      qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/**
 * Attach a SECONDARY file input to a chain operation (KAN-1011) — the
 * artwork for a tampon print, the logo for an engraving. The part itself
 * comes from the previous step, so this file rides the operation's
 * ``config_snapshot`` rather than the part-upload route.
 */
export function useUploadOperationFile(
  options?: UseMutationOptions<
    ItemOperation,
    unknown,
    { operationId: number; key: string; file: File }
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    ItemOperation,
    unknown,
    { operationId: number; key: string; file: File }
  >({
    mutationFn: ({ operationId, key, file }) => {
      const body = new FormData();
      body.append("file", file);
      return apiFetch<ItemOperation>(
        `${API}/cart/operations/${operationId}/files/${encodeURIComponent(key)}`,
        { method: "POST", body },
      );
    },
    ...restOptions,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/** Drop a secondary file input from a chain operation (KAN-1011). */
export function useRemoveOperationFile(
  options?: UseMutationOptions<
    ItemOperation,
    unknown,
    { operationId: number; key: string }
  >,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<
    ItemOperation,
    unknown,
    { operationId: number; key: string }
  >({
    mutationFn: ({ operationId, key }) =>
      apiFetch<ItemOperation>(
        `${API}/cart/operations/${operationId}/files/${encodeURIComponent(key)}`,
        { method: "DELETE" },
      ),
    ...restOptions,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/**
 * Remove an operation from an item's chain. Same cache invalidation
 * as add — keeps the dropdown + cart view in sync.
 */
export function useRemoveOperation(
  itemSlug: string,
  options?: UseMutationOptions<void, unknown, number>,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<void, unknown, number>({
    mutationFn: (operationId) =>
      apiFetch<void>(`${API}/cart/operations/${operationId}`, {
        method: "DELETE",
      }),
    ...restOptions,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({
        queryKey: processQueryKeys.chainContinuations(itemSlug),
      });
      qc.invalidateQueries({ queryKey: ["cart"] });
      await callerOnSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}
